import { useEffect, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from "./icons.jsx";
import { iso, parseISO, fmtShort, fmtLong } from "../lib/dates.js";

// Two-month range calendar with hover preview. Shared between HeroSearch,
// PropertyPage's BookingBar, and ResultsPage's SearchBar.

// Only as many week rows as the month needs — a fixed six left a blank row
// under most months. Matches HeroSearch's calendar.
function monthDays(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Math.ceil((first.getDay() + daysInMonth) / 7) * 7;
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: cells }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

function CalendarMonth({ month, today, start, end, phase, hover, onHover, onPick }) {
  return (
    <div className="w-full">
      <h4 className="text-center font-semibold text-sm text-ink mb-2">{month.toLocaleDateString("en-US",{month:"long",year:"numeric"})}</h4>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {monthDays(month).map(day => {
          const val = iso(day);
          const outside = day.getMonth() !== month.getMonth();
          const past = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const previewEnd = !end && phase === "end" && hover > start ? hover : "";
          const rangeEnd = end || previewEnd;
          const inRange = !!start && !!rangeEnd && val > start && val < rangeEnd;
          const isPreview = val === previewEnd;
          const isStart = val === start;
          const isEnd = val === end || isPreview;
          const disabled = past || outside;
          let classes = "relative aspect-square flex items-center justify-center text-sm rounded-full font-medium select-none";
          if (disabled) classes += " text-ink-muted/40 cursor-not-allowed";
          else classes += " cursor-pointer hover:bg-ocean-50 text-ink";
          if (outside) classes += " invisible";
          if (inRange) classes += " !rounded-none bg-ocean-100/70 text-ocean-800";
          if (isStart || isEnd) classes += " bg-ocean-600 text-white hover:bg-ocean-700 z-10";
          if (isStart && rangeEnd) classes += " !rounded-l-full !rounded-r-none";
          if (isEnd && start && val !== start) classes += " !rounded-r-full !rounded-l-none";
          if (isStart && isEnd) classes += " !rounded-full";
          return (
            <button type="button" key={val} disabled={disabled} className={classes}
              onClick={() => onPick(val)}
              onMouseEnter={() => onHover(val)}
              onMouseLeave={() => onHover("")}>{day.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
}

// Compact popover trigger for a date range — used inside search bars.
// - Two field-style buttons (Check-in / Check-out) side by side act as the trigger
// - Clicking either opens a floating two-month calendar
// - Range picking, hover preview, clear, apply, arrow-nav months included
export default function RangeCalendarField({ checkin, checkout, onChange, className = "" }) {
  const today = new Date();
  today.setHours(0,0,0,0);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(checkin && !checkout ? "end" : "start");
  const [hover, setHover] = useState("");
  const [calMonth, setCalMonth] = useState(() => new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1));
  const [popRect, setPopRect] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    const onDown = e => {
      if (rootRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Position popover in the viewport, clamped so its right edge never crashes
  // past the window. Popover width is ~min(720, 92vw); leave a 16px gutter.
  useEffect(() => {
    if (!open || !rootRef.current) return;
    const compute = () => {
      const rect = rootRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const popWidth = Math.min(720, vw * 0.92);
      const left = Math.min(rect.left, vw - popWidth - 16);
      setPopRect({ top: rect.bottom + 8, left: Math.max(16, left) });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open]);

  const pick = v => {
    if (phase === "start" || !checkin || v <= checkin) {
      onChange(v, "");
      setPhase("end");
      setHover("");
    } else {
      onChange(checkin, v);
      setPhase("start");
      setHover("");
    }
  };

  const openTo = which => {
    setOpen(true);
    setPhase(which);
    setHover("");
    setCalMonth(new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1));
  };

  return (
    <div ref={rootRef} className={`relative flex items-stretch ${className}`}>
      <button
        type="button"
        onClick={() => openTo("start")}
        className="flex items-center gap-2 px-4 py-3 rounded-pill border border-ink/10 bg-white md:rounded-none md:border-0 md:border-r md:border-ink/10 md:bg-transparent text-left"
      >
        <CalendarIcon size={14} className="text-ink-muted shrink-0"/>
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Check-in</span>
          <span className={`text-sm ${checkin ? "text-ink" : "text-ink-muted"}`}>{checkin ? fmtShort(checkin) : "Add date"}</span>
        </div>
      </button>
      <button
        type="button"
        onClick={() => openTo("end")}
        className="flex items-center gap-2 px-4 py-3 rounded-pill border border-ink/10 bg-white md:rounded-none md:border-0 md:border-r md:border-ink/10 md:bg-transparent text-left"
      >
        <CalendarIcon size={14} className="text-ink-muted shrink-0"/>
        <div className="flex flex-col">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Check-out</span>
          <span className={`text-sm ${checkout ? "text-ink" : "text-ink-muted"}`}>{checkout ? fmtShort(checkout) : "Add date"}</span>
        </div>
      </button>

      {open && (
        <div
          ref={popRef}
          className="fixed z-[60] w-[min(720px,92vw)] rounded-3xl bg-white shadow-cardHover ring-1 ring-ink/5 p-5 text-left"
          style={{ top: popRect.top, left: popRect.left }}
        >
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-in</div>
                <div className="font-semibold text-ink">{checkin ? fmtLong(checkin) : "Select"}</div>
              </div>
              <div className="text-ink-muted">→</div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-out</div>
                <div className="font-semibold text-ink">{checkout ? fmtLong(checkout) : "Select"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()-1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Previous month"><ChevronLeftIcon size={16}/></button>
              <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Next month"><ChevronRightIcon size={16}/></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CalendarMonth month={calMonth} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={pick}/>
            <CalendarMonth month={new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1)} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={pick}/>
          </div>
          <div className="mt-4 pt-3 border-t border-ink/5 flex items-center justify-between">
            <button type="button" onClick={() => { onChange("", ""); setPhase("start"); setHover(""); }} className="text-sm text-ink-muted hover:text-ink underline">Clear dates</button>
            <button type="button" onClick={() => setOpen(false)} className="btn-primary py-2 px-6 text-sm" disabled={!checkin || !checkout}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
